import React, { useState } from 'react';
import { Submission, Judgment, EvaluationStatus } from 'types/evaluation';
import EvaluationStatsHeader from './EvaluationStatsHeader';
import EvaluationFilters from './EvaluationFilters';
import SubmissionsList from './SubmissionsList';
import CompleteAllButton from './CompleteAllButton';

interface SubmissionsOverviewProps {
  submissions: Submission[];
  existingJudgments: Record<string, Judgment>;
  onSelectSubmission: (submission: Submission) => void;
  onCompleteAll: () => void;
  isCompletingAll: boolean;
  userCategory: string;
  error?: string | null;
  onRetry?: () => void;
  validUserCategories?: string[];
}

const SubmissionsOverview: React.FC<SubmissionsOverviewProps> = ({
  submissions,
  existingJudgments,
  onSelectSubmission,
  onCompleteAll,
  isCompletingAll,
  userCategory,
  error,
  onRetry,
  validUserCategories,
}) => {
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'not-started', 'draft', 'completed'
  const [filterCategory, setFilterCategory] = useState('all'); // 'all' or specific category
  const [searchQuery, setSearchQuery] = useState(''); // Search functionality

  // Get unique categories from submissions
  const availableCategories = [
    ...new Set(submissions.map(s => s.category)),
  ].sort();

  // Get judge's assigned categories (for filter options)
  const judgeCategories = userCategory.includes(',')
    ? userCategory
        .split(',')
        .map(c => c.trim())
        .sort()
    : [userCategory].filter(Boolean);

  // Combine both submission categories and judge categories for filter options
  const allFilterCategories = [
    ...new Set([...availableCategories, ...judgeCategories]),
  ].sort();

  // Get evaluation status for a submission
  const getEvaluationStatus = (submission: Submission): EvaluationStatus => {
    const submissionId = String(submission.documentId || submission.id);
    const judgment = existingJudgments[submissionId];

    if (!judgment) {
      return { status: 'not-started', label: 'Not Started', color: 'gray' };
    }

    if (judgment.overall_summary === 'Draft') {
      return { status: 'draft', label: 'Draft', color: 'yellow' };
    }

    return { status: 'completed', label: 'Completed', color: 'green' };
  };

  // Filter submissions by status, category, and search query
  const filteredSubmissions = submissions
    .filter(submission => {
      // Status filter
      const statusMatch =
        filterStatus === 'all' ||
        getEvaluationStatus(submission).status === filterStatus;

      // Category filter
      const categoryMatch =
        filterCategory === 'all' || submission.category === filterCategory;

      // Search filter - search in project name, submitter name, organization, and pillar
      const searchMatch =
        searchQuery === '' ||
        [
          submission.description,
          submission.project_name,
          submission.title,
          submission.full_name,
          submission.pillar,
          submission.org_unit,
          submission.category,
        ].some(
          field =>
            field && field.toLowerCase().includes(searchQuery.toLowerCase())
        );

      return statusMatch && categoryMatch && searchMatch;
    })
    .sort((a, b) => {
      // Default sort by project name
      return (a.project_name || a.full_name || '').localeCompare(
        b.project_name || b.full_name || ''
      );
    });

  // Count submissions by status (considering category filter)
  const relevantSubmissions =
    filterCategory === 'all'
      ? submissions
      : submissions.filter(s => s.category === filterCategory);

  const statusCounts = relevantSubmissions.reduce(
    (acc: Record<string, number>, submission) => {
      const status = getEvaluationStatus(submission).status;
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    },
    {}
  );

  // Count submissions by category (considering status filter)
  const categoryCounts: Record<string, number> = {};
  allFilterCategories.forEach(category => {
    const categorySubmissions = submissions.filter(
      s => s.category === category
    );
    const filteredCategorySubmissions =
      filterStatus === 'all'
        ? categorySubmissions
        : categorySubmissions.filter(
            s => getEvaluationStatus(s).status === filterStatus
          );
    categoryCounts[category] = filteredCategorySubmissions.length;
  });

  const totalSubmissions = relevantSubmissions.length;
  const notStarted = statusCounts['not-started'] || 0;
  const drafts = statusCounts['draft'] || 0;
  const completed = statusCounts['completed'] || 0;

  // Check if all evaluations are complete (either completed or draft)
  const allEvaluated = notStarted === 0;
  const hasAnyDrafts = drafts > 0;

  return (
    <div className="space-y-6">
      {/* Header with Statistics */}
      <EvaluationStatsHeader
        userCategory={userCategory}
        totalSubmissions={totalSubmissions}
        notStarted={notStarted}
        drafts={drafts}
        completed={completed}
      />

      {/* Filters Section - Following SMDDJudgmentPage pattern */}
      <EvaluationFilters
        totalSubmissions={totalSubmissions}
        notStarted={notStarted}
        drafts={drafts}
        completed={completed}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        filterStatus={filterStatus}
        onFilterStatusChange={setFilterStatus}
        filterCategory={filterCategory}
        onFilterCategoryChange={setFilterCategory}
        allFilterCategories={allFilterCategories}
        categoryCounts={categoryCounts}
        userCategory={userCategory}
        submissionsCount={submissions.length}
        filteredCount={filteredSubmissions.length}
        onClearFilters={() => {
          setFilterStatus('all');
          setFilterCategory('all');
          setSearchQuery('');
        }}
      />

      {/* Submissions List */}
      <SubmissionsList
        filteredSubmissions={filteredSubmissions}
        error={error}
        validUserCategories={validUserCategories}
        onRetry={onRetry}
        onSelectSubmission={onSelectSubmission}
        getEvaluationStatus={getEvaluationStatus}
      />

      {/* Complete All Evaluations Button - Bottom Right */}
      <CompleteAllButton
        onCompleteAll={onCompleteAll}
        isCompletingAll={isCompletingAll}
        allEvaluated={allEvaluated}
        hasAnyDrafts={hasAnyDrafts}
      />
    </div>
  );
};

export default SubmissionsOverview;
